import { useContext, useState, useMemo, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import BreadCrumb from "../breadCrumb/BreadCrumb";
import EprOverviewDao from "../../dao/EprOverviewDao";
import LocationDao from "../../dao/LocationDao";
import LoadingSvg from "../loadingSvg/LoadingSvg";
import Icon from "../icon/Icon";
import TabButton from "../formInputs/buttons/TabButton.jsx";

import { GlobalContext } from "../contextProvider/ContextProvider";
import {
    buildRegionSchoolPool,
    compareMetrics,
    deriveRegionOptions,
    enrichRegionMetrics,
    filterRegionLocations,
    filterSchoolsWithAnyTemplate
} from "../../utils/teacherOverview/RegionOverviewUtils";
import useOverviewSchoolYear from "../../hooks/useOverviewSchoolYear";
import {
    formatSchoolYearName,
    shouldDisableSecondSemesterTab,
    writeOverviewSchoolYearHandoff,
    INIT_OVERVIEW_SCHOOL_YEAR_KEY
} from "./overviewSchoolYear";

import "../../styles/ManageEprContent.scss";
import "../../styles/StudentRosterTable.scss";
import "../../styles/RegionOverviewHome.scss";
import "../../styles/OverviewSchoolYearSelect.scss";

const GRADE_TAG_MAP = [
    { display: "K", tagValues: ["KINDERGARTEN"] },
    { display: "1st", tagValues: ["FIRST"] },
    { display: "2nd", tagValues: ["SECOND"] },
    { display: "3rd", tagValues: ["THIRD"] },
    { display: "4th", tagValues: ["FOURTH"] },
    { display: "5th", tagValues: ["FIFTH"] },
    { display: "6th", tagValues: ["SIXTH"] }
];

const getStatusFormatting = (inTags, inReleased) => {
    if (!inTags) return { iconName: "NA", fill: "#999999" };
    return inReleased ? { iconName: "CHECK_CIRCLE", fill: "#698B26" } : { iconName: "X_CIRCLE", fill: "#CB321A" };
};

/**
 * RegionOverviewHome component for District Administrators to view regions
 * @returns {JSX.Element}
 */
const RegionOverviewHome = () => {
    const { state } = useContext(GlobalContext);
    const { allLocationDtos, legacyLocationDtos, token, currentTimePeriodDto, viewTimePeriodDto } = state || {};
    const { years, selectedKey, setSelectedKey, activeKey, isHistorical, options, sy } = useOverviewSchoolYear();

    const [selectedRegion, setSelectedRegion] = useState("");
    const [metricsData, setMetricsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewingSemester, setViewingSemester] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "locationName", direction: "ascending" });

    const selectedSemesterName =
        viewingSemester || currentTimePeriodDto?.name || viewTimePeriodDto?.name || "1st Semester";
    const semesterKey = selectedSemesterName === "1st Semester" ? "firstSemester" : "secondSemester";
    const currentPeriodName = currentTimePeriodDto?.name || viewTimePeriodDto?.name;
    const disableSecondTab = shouldDisableSecondSemesterTab({
        selectedKey,
        activeKey,
        currentPeriodName
    });

    useEffect(() => {
        if (disableSecondTab && selectedSemesterName === "2nd Semester") {
            setViewingSemester("1st Semester");
        }
    }, [disableSecondTab, selectedSemesterName]);

    // Retired schools only have data on past years, so they join the pool only for historical selections.
    const schoolPool = useMemo(
        () => buildRegionSchoolPool({ allLocationDtos, legacyLocationDtos, isHistorical }),
        [allLocationDtos, legacyLocationDtos, isHistorical]
    );

    const regions = useMemo(() => deriveRegionOptions(schoolPool), [schoolPool]);

    useEffect(() => {
        if (selectedRegion && regions.length && !regions.includes(selectedRegion)) {
            setSelectedRegion("");
        }
    }, [regions, selectedRegion]);

    const labelObj = [
        { label: "Home", path: "/home" },
        { label: "Report Card Overview", path: "/home/report_card_overview" }
    ];

    /**
     * Handles sorting logic when a column header is clicked.
     * @param {string} key
     */
    const requestSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "ascending" ? "descending" : "ascending"
        }));
    };

    /**
     * Renders sort icons for a column header.
     * @param {string} columnKey
     * @returns {JSX.Element}
     */
    const renderSortIcons = (columnKey) => {
        if (sortConfig.key === columnKey) {
            if (sortConfig.direction === "ascending") {
                return (
                    <span className="icon-container">
                        <i className="bi bi-caret-up-fill sort-icon" />
                        <i className="bi bi-caret-down sort-icon-bottom" />
                    </span>
                );
            } else {
                return (
                    <span className="icon-container">
                        <i className="bi bi-caret-up sort-icon" />
                        <i className="bi bi-caret-down-fill sort-icon-bottom" />
                    </span>
                );
            }
        }
        return (
            <span className="icon-container">
                <i className="bi bi-caret-up sort-icon" />
                <i className="bi bi-caret-down sort-icon-bottom" />
            </span>
        );
    };

    /**
     * Handles selection of a region
     * @param {Object} e - Event object
     */
    const handleRegionChange = (e) => {
        setSelectedRegion(e.target.value);
    };

    const handleSchoolYearChange = (e) => {
        const next = e.target.value;
        setSelectedKey(Number.isNaN(Number(next)) ? next : Number(next));
    };

    useEffect(() => {
        if (!selectedRegion || !token) {
            setMetricsData([]);
            return undefined;
        }

        const regionLocations = filterRegionLocations(schoolPool, selectedRegion);
        const locationKeys = regionLocations.map((loc) => loc.key).join(",");

        if (!locationKeys) return undefined;

        setLoading(true);

        const metricsPromise = EprOverviewDao({
            action: "regionLocationMetricsRead",
            locationKeys,
            token,
            ...(sy != null ? { schoolYearKey: sy } : {})
        });

        const locationsPromise = LocationDao({
            action: "locationsRead",
            params: { locationIds: locationKeys },
            token
        });

        let cancelled = false;
        Promise.allSettled([metricsPromise, locationsPromise])
            .then(([metricsResult, locationsResult]) => {
                if (cancelled) return;
                const metricsPayload = metricsResult.status === "fulfilled" ? metricsResult.value?.data?.payload : null;
                const locationsPayload =
                    locationsResult.status === "fulfilled" ? locationsResult.value?.data?.payload : null;

                let metricsArray = [];
                if (metricsPayload) {
                    metricsArray = Array.isArray(metricsPayload) ? metricsPayload : Object.values(metricsPayload);
                }

                const locationsArray =
                    locationsPayload?.results || (Array.isArray(locationsPayload) ? locationsPayload : []);

                const enriched = enrichRegionMetrics(regionLocations, metricsArray, locationsArray);
                setMetricsData(isHistorical ? filterSchoolsWithAnyTemplate(enriched) : enriched);
            })
            .catch((error) => {
                console.error("Error fetching region metrics:", error);
                if (!cancelled) setMetricsData([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [selectedRegion, token, schoolPool, sy, isHistorical]);

    const sortedMetrics = useMemo(() => {
        if (!metricsData) return [];
        return [...metricsData].sort((a, b) =>
            compareMetrics(a, b, { key: sortConfig.key, direction: sortConfig.direction, semesterKey })
        );
    }, [metricsData, sortConfig, semesterKey]);

    const selectedSchoolYear = years.find((year) => String(year.key) === String(selectedKey));
    const schoolYearName = formatSchoolYearName(selectedSchoolYear?.name);
    const pageTitle = `Report Card - Region Overview${schoolYearName ? ` (${schoolYearName})` : ""}`;

    return (
        <>
            <BreadCrumb labelObj={labelObj} />
            <div className="gutter-95">
                <div className="manage-header-container region-overview-header">
                    <h4>{pageTitle}</h4>
                </div>

                <div className="selection-container region-selection-container">
                    <label className="select-label" htmlFor="region-select">
                        <div>Select a Region:</div>
                        <select
                            id="region-select"
                            name="region-select"
                            className="content-area-select"
                            value={selectedRegion}
                            onChange={handleRegionChange}
                        >
                            <option value="" disabled>
                                {regions.length > 0 ? "Select a Region" : "No regions found"}
                            </option>
                            {regions.map((region) => (
                                <option key={region} value={region}>
                                    {region}
                                </option>
                            ))}
                        </select>
                    </label>

                    {selectedRegion && (
                        <div className="semester-tabs region-semester-tabs">
                            <label className="school-year-select">
                                <span className="visually-hidden">School Year</span>
                                <select
                                    aria-label="School Year"
                                    className="overview-school-year-select"
                                    value={selectedKey ?? ""}
                                    onChange={handleSchoolYearChange}
                                    disabled={!options.length}
                                >
                                    {options.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <TabButton
                                className={
                                    selectedSemesterName === "1st Semester" ? "tab-button-selected" : "tab-button"
                                }
                                onClick={() => {
                                    if (selectedSemesterName !== "1st Semester") {
                                        setLoading(true);
                                        setViewingSemester("1st Semester");
                                        setTimeout(() => setLoading(false), 100);
                                    }
                                }}
                                size="small-no-width"
                            >
                                <div>
                                    <p className="primary">1st Semester</p>
                                </div>
                            </TabButton>
                            <TabButton
                                className={
                                    selectedSemesterName === "2nd Semester" ? "tab-button-selected" : "tab-button"
                                }
                                disable={disableSecondTab}
                                onClick={() => {
                                    if (selectedSemesterName !== "2nd Semester") {
                                        setLoading(true);
                                        setViewingSemester("2nd Semester");
                                        setTimeout(() => setLoading(false), 100);
                                    }
                                }}
                                size="small-no-width"
                                width={135}
                            >
                                <div>
                                    <p className="primary">2nd Semester</p>
                                </div>
                            </TabButton>
                        </div>
                    )}
                </div>

                {selectedRegion && <h5 className="region-name-title">{selectedRegion} Region</h5>}

                {loading ? (
                    <div className="region-loading-container">
                        <LoadingSvg />
                    </div>
                ) : (
                    selectedRegion && (
                        <div className="student-roster-table differentiation-table region-table-wrapper">
                            <table className="table table-striped region-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <button
                                                type="button"
                                                onClick={() => requestSort("locationName")}
                                                className="table-header-button"
                                                aria-label={isHistorical ? "Sort by name" : "Sort by school name"}
                                            >
                                                <span className="header-btn-label">
                                                    {isHistorical ? "Name" : "School Name"}
                                                </span>
                                                <span className="header-btn-sort">
                                                    {renderSortIcons("locationName")}
                                                </span>
                                            </button>
                                        </th>
                                        {!isHistorical && (
                                            <th className="th-w-120">
                                                <button
                                                    type="button"
                                                    onClick={() => requestSort("templateCreated")}
                                                    className="table-header-button"
                                                    aria-label="Sort by School Template Created"
                                                >
                                                    <span className="header-btn-label">Template Created</span>
                                                    <span className="header-btn-sort">
                                                        {renderSortIcons("templateCreated")}
                                                    </span>
                                                </button>
                                            </th>
                                        )}
                                        {!isHistorical && (
                                            <th className="th-w-280">
                                                <button
                                                    type="button"
                                                    onClick={() => requestSort("releasedGrades")}
                                                    className="table-header-button"
                                                    aria-label="Sort by released grades"
                                                >
                                                    <span className="header-btn-label">Released Grades</span>
                                                    <span className="header-btn-sort">
                                                        {renderSortIcons("releasedGrades")}
                                                    </span>
                                                </button>
                                            </th>
                                        )}
                                        {!isHistorical && (
                                            <th>
                                                <button
                                                    type="button"
                                                    onClick={() => requestSort("slesUsed")}
                                                    className="table-header-button"
                                                    aria-label="Sort by SLEs setup"
                                                >
                                                    <span className="header-btn-label">SLEs Setup</span>
                                                    <span className="header-btn-sort">
                                                        {renderSortIcons("slesUsed")}
                                                    </span>
                                                </button>
                                            </th>
                                        )}
                                        <th>
                                            <button
                                                type="button"
                                                onClick={() => requestSort("eprsReleasedRatio")}
                                                className="table-header-button"
                                                aria-label="Sort by released to guardians"
                                            >
                                                <span className="header-btn-label-with-icon">
                                                    Released to Guardians
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id="released-guardians-tooltip">
                                                                Count of all released grading profiles created by
                                                                homeroom teachers at location
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <i className="bi bi-info-circle-fill info-icon"></i>
                                                    </OverlayTrigger>
                                                </span>
                                                <span className="header-btn-sort">
                                                    {renderSortIcons("eprsReleasedRatio")}
                                                </span>
                                            </button>
                                        </th>
                                        <th>
                                            <button
                                                type="button"
                                                onClick={() => requestSort("guardianAnalytics")}
                                                className="table-header-button"
                                                aria-label="Sort by guardians viewed"
                                            >
                                                <span className="header-btn-label">Guardians Viewed</span>
                                                <span className="header-btn-sort">
                                                    {renderSortIcons("guardianAnalytics")}
                                                </span>
                                            </button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedMetrics.length > 0 ? (
                                        sortedMetrics.map((metric) => (
                                            <tr key={metric.locationKey}>
                                                <td
                                                    className="td-location-name"
                                                    data-label={isHistorical ? "Name" : "School Name"}
                                                >
                                                    <button
                                                        type="button"
                                                        className="btn-link p-0 m-0 border-0 bg-transparent text-primary text-decoration-underline"
                                                        style={{ textAlign: "left" }}
                                                        onClick={() => {
                                                            writeOverviewSchoolYearHandoff(selectedKey);
                                                            sessionStorage.setItem(
                                                                "initViewedSchoolKey",
                                                                metric.locationKey
                                                            );
                                                            window.open("/home/overview", "_blank");
                                                            sessionStorage.removeItem("initViewedSchoolKey");
                                                            sessionStorage.removeItem(INIT_OVERVIEW_SCHOOL_YEAR_KEY);
                                                        }}
                                                    >
                                                        {metric.locationName}
                                                    </button>
                                                </td>
                                                {!isHistorical && (
                                                    <td data-label="Template Created">
                                                        {(() => {
                                                            const status = metric[`${semesterKey}TemplateCreated`];
                                                            let iconName = "NA";
                                                            let fill = "#999999";

                                                            if (status === true) {
                                                                iconName = "CHECK_CIRCLE";
                                                                fill = "#698B26";
                                                            } else if (status === false) {
                                                                iconName = "X_CIRCLE";
                                                                fill = "#CB321A";
                                                            }

                                                            return (
                                                                <Icon
                                                                    height={20}
                                                                    iconName={iconName}
                                                                    fill={fill}
                                                                    width={20}
                                                                />
                                                            );
                                                        })()}
                                                    </td>
                                                )}
                                                {!isHistorical && (
                                                    <td data-label="Released Grades">
                                                        <div className="grade-tags">
                                                            {GRADE_TAG_MAP.map(({ display, tagValues }) => {
                                                                const inTags = tagValues.some((t) =>
                                                                    (metric.tags || []).includes(t)
                                                                );
                                                                const inReleased = (
                                                                    metric[`${semesterKey}ReleasedGrades`] || []
                                                                ).includes(display);
                                                                const { iconName, fill } = getStatusFormatting(
                                                                    inTags,
                                                                    inReleased
                                                                );
                                                                return (
                                                                    <div key={display} className="grade-tag">
                                                                        <span className="grade-tag-label">
                                                                            {display}
                                                                        </span>
                                                                        <Icon
                                                                            height={16}
                                                                            width={16}
                                                                            iconName={iconName}
                                                                            fill={fill}
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </td>
                                                )}
                                                {!isHistorical && (
                                                    <td data-label="SLEs Setup">
                                                        <Icon
                                                            height={20}
                                                            iconName={metric.slesUsed ? "CHECK_CIRCLE" : "X_CIRCLE"}
                                                            fill={metric.slesUsed ? "#698B26" : "#CB321A"}
                                                            width={20}
                                                        />
                                                    </td>
                                                )}
                                                <td data-label="Released to Guardians">
                                                    {metric.eprsReleasedRatio?.[semesterKey] || "N/A"}
                                                </td>
                                                <td data-label="Guardians Viewed">
                                                    {metric.guardianAnalytics?.[semesterKey] || "N/A"}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={isHistorical ? "3" : "6"}>
                                                No metrics found for this region.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )
                )}
            </div>
        </>
    );
};

export default RegionOverviewHome;
